/**
 * useUser Hook
 * Provides access to current user state and permissions
 */

import { useAtomValue } from "jotai";
import {
  userStateAtom,
  msalAccountAtom,
  isAuthenticatedAtom,
  organizationSettingsAtom,
  hoursInDayAtom,
  disableFravaerAtom,
  userRoleLevelAtom,
  isAdminAtom,
  isManagerAtom,
  isSalesAtom,
  userDisplayNameAtom,
  userEmailAtom,
  pagesAccessibleAtom,
  userPhotoAtom,
  hasAccessToAtom,
  ROLE_ADMIN,
  ROLE_MANAGER,
  ROLE_SALES,
  ROLE_EMPLOYEE,
} from "@/store/userStore";
import type { IUserState, IOrganizationSettings, IMenuItem } from "@/services";
import type { AccountInfo } from "@azure/msal-browser";

export interface UseUserReturn {
  /** Full user state from API */
  user: IUserState | null;
  /** MSAL account info */
  msalAccount: AccountInfo | null;
  /** Is user authenticated */
  isAuthenticated: boolean;
  /** User display name */
  displayName: string;
  /** User email */
  email: string;
  /** User profile photo (base64 data URL) */
  photo: string;
  /** User role level */
  roleLevel: number;
  /** Is user an admin */
  isAdmin: boolean;
  /** Is user a manager or higher */
  isManager: boolean;
  /** Is user sales or higher */
  isSales: boolean;
  /** Organization settings */
  organizationSettings: IOrganizationSettings;
  /** Hours in day setting */
  hoursInDay: number;
  /** Is fravær disabled */
  disableFravaer: boolean;
  /** Pages accessible to the user */
  pagesAccessible: IMenuItem[];
  /** Check if user has access to a route */
  hasAccessTo: (route: string) => boolean;
  /** Check if user has at least the given role level */
  hasRole: (level: number) => boolean;
}

export function useUser(): UseUserReturn {
  const user = useAtomValue(userStateAtom);
  const msalAccount = useAtomValue(msalAccountAtom);
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const displayName = useAtomValue(userDisplayNameAtom);
  const email = useAtomValue(userEmailAtom);
  const photo = useAtomValue(userPhotoAtom);
  const roleLevel = useAtomValue(userRoleLevelAtom);
  const isAdmin = useAtomValue(isAdminAtom);
  const isManager = useAtomValue(isManagerAtom);
  const isSales = useAtomValue(isSalesAtom);
  const organizationSettings = useAtomValue(organizationSettingsAtom);
  const hoursInDay = useAtomValue(hoursInDayAtom);
  const disableFravaer = useAtomValue(disableFravaerAtom);
  const pagesAccessible = useAtomValue(pagesAccessibleAtom);
  const hasAccessTo = useAtomValue(hasAccessToAtom);

  // Check if user has at least the given role level (lower number = higher access)
  const hasRole = (level: number): boolean => {
    return roleLevel <= level;
  };

  return {
    user,
    msalAccount,
    isAuthenticated,
    displayName,
    email,
    photo,
    roleLevel,
    isAdmin,
    isManager,
    isSales,
    organizationSettings,
    hoursInDay,
    disableFravaer,
    pagesAccessible,
    hasAccessTo,
    hasRole,
  };
}

// Re-export role constants for convenience
export { ROLE_ADMIN, ROLE_MANAGER, ROLE_SALES, ROLE_EMPLOYEE };
